import React from "react";
import { Card, Spin, Typography, Alert, Space, Tooltip } from "antd";
import {
  FunnelPlotOutlined,
  ReloadOutlined,
  SyncOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import FunnelProgressBars, { FunnelFilterType } from "./FunnelProgressBars";
import useApplicationStats from "../../hooks/useApplicationStats";
import "./index.scss";

const { Text } = Typography;

interface FunnelChartSectionProps {
  jobPostId: string;
  themeColor?: string;
}

// Format time ago
const formatTimeAgo = (date: Date | null): string => {
  if (!date) return "";
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
};

const FunnelChartSection: React.FC<FunnelChartSectionProps> = ({
  jobPostId,
  themeColor,
}) => {
  const [, setSearchParams] = useSearchParams();
  const { data, loading, error, refresh, lastUpdated } = useApplicationStats(
    jobPostId,
    true
  );

  // Handle filter click - navigate to applications tab with filter
  const handleFilterClick = (filter: FunnelFilterType) => {
    const params: Record<string, string> = {
      tabIndex: "applications",
    };

    switch (filter.type) {
      case "profileMatchStatus":
        params.profileMatchStatus = filter.value;
        break;
      case "roundStatus":
        params.roundStatus = filter.value;
        break;
      case "status":
        params.status = filter.value;
        break;
      case "all":
        break;
    }

    setSearchParams(params);
  };

  return (
    <Card
      className="funnel-chart-section-card"
      size="small"
      title={
        <div className="funnel-card-header">
          <span className="funnel-card-title">
            <FunnelPlotOutlined /> Application Funnel
          </span>
          <div className="funnel-card-actions">
            <Space size={8}>
              {lastUpdated && (
                <Tooltip title="Auto-refreshes every 2 minutes">
                  <Text type="secondary" className="last-updated">
                    <ClockCircleOutlined /> {formatTimeAgo(lastUpdated)}
                  </Text>
                </Tooltip>
              )}
              <Tooltip title="Refresh now">
                <a onClick={refresh} className="refresh-btn">
                  <SyncOutlined spin={loading} />
                </a>
              </Tooltip>
            </Space>
          </div>
        </div>
      }
    >
      {loading && !data && (
        <div className="funnel-loading">
          <Spin />
          <Text type="secondary">Loading funnel data...</Text>
        </div>
      )}

      {error && !loading && !data && (
        <Alert
          type="error"
          message="Failed to load data"
          description={error}
          action={
            <a onClick={refresh}>
              <ReloadOutlined /> Retry
            </a>
          }
          showIcon
        />
      )}

      {data && (
        <FunnelProgressBars
          data={data}
          themeColor={themeColor}
          onFilterClick={handleFilterClick}
        />
      )}
    </Card>
  );
};

export default FunnelChartSection;
